// Contatos do embarcador (PO1-3384, H3 PO1-3387): quem recebe e-mails do
// embarcador sem ter usuário no Hub (caixas de grupo, TI, logística, o resumo
// diário de faturamento). Mesmo molde da aba Contatos do cadastro da
// transportadora (MeuFrete): tabela + modal criar/editar, pelo embarcador
// escolhido no topo. Aqui também dá para remover (com confirmação na tela).
// Nos onChange, o valor do campo é lido ANTES do setState (event pooling do
// React 16).
//
// Sprint 638: a tela virou a aba "Contatos" do cadastro do embarcador
// (views/Empresa/Embarcadores). Com `embutido`, não desenha o PageHero e usa o
// `embarcadorId` recebido por prop em vez do embarcador do topo.
import React, { useCallback, useEffect, useState } from "react";
import { Button, Col, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, Row, Spinner, Table } from "reactstrap";

import PageHero, { PageHeroRefreshButton } from "components/PageHero";
import ShellCard from "components/ShellCard";
import SpecificEmpresaRequiredNotice from "components/SpecificEmpresaRequiredNotice";
import { AUTOPILOT_COLORS } from "components/autopilot/styles";
import { useCompanies } from "hooks/companies";
import { useToast } from "hooks/toast";
import {
  listarTiposContatoEmbarcador,
  listarContatosEmbarcador,
  criarContatoEmbarcador,
  atualizarContatoEmbarcador,
  removerContatoEmbarcador,
  mensagemErroContato,
} from "services/embarcadorContatosApi";

const mutedStyle = { color: AUTOPILOT_COLORS.muted, fontSize: "0.8rem" };
const stateBoxStyle = { display: "flex", alignItems: "center", gap: "0.6rem", padding: "1rem 0.25rem", color: AUTOPILOT_COLORS.muted, fontSize: "0.9rem" };
const labelStyle = { fontSize: "0.78rem", color: AUTOPILOT_COLORS.muted, marginBottom: 4 };
const CONTATO_VAZIO = { nome: "", email: "", idTipoContato: "", dddTelefone: "", telefone: "", dddCelular: "", celular: "" };

function telefone(ddd, numero) {
  if (!numero) return "";
  return ddd ? `(${ddd}) ${numero}` : numero;
}

function formDe(c) {
  return {
    nome: c.nome || "",
    email: c.email || "",
    idTipoContato: c.idTipoContato || "",
    dddTelefone: c.dddTelefone || "",
    telefone: c.telefone || "",
    dddCelular: c.dddCelular || "",
    celular: c.celular || "",
  };
}

export default function ContatosEmbarcador({ embarcadorId: embarcadorIdProp, embarcadorNome, embutido = false } = {}) {
  const { addToast } = useToast();
  const { empresa, empresaId, hasEmpresaSelection } = useCompanies();
  const embarcadorId = embutido ? embarcadorIdProp || null : hasEmpresaSelection ? empresaId : null;
  const nomeEmbarcador = embutido ? embarcadorNome : empresa && empresa.label;

  const [tipos, setTipos] = useState([]);
  const [contatos, setContatos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [modal, setModal] = useState(null); // { modo: 'novo'|'editar', contato }
  const [form, setForm] = useState(CONTATO_VAZIO);
  const [remover, setRemover] = useState(null); // contato a confirmar

  const carregar = useCallback(async () => {
    if (!embarcadorId) return;
    setLoading(true);
    setErro("");
    try {
      const [t, c] = await Promise.all([listarTiposContatoEmbarcador(), listarContatosEmbarcador(embarcadorId)]);
      setTipos(Array.isArray(t) ? t : []);
      setContatos(Array.isArray(c) ? c : []);
    } catch (err) {
      setErro(mensagemErroContato(err, "Não foi possível carregar os contatos."));
    } finally {
      setLoading(false);
    }
  }, [embarcadorId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const campo = (nome) => (e) => {
    const valor = e.target.value;
    setForm((f) => ({ ...f, [nome]: valor }));
  };

  const abrirNovo = () => {
    setForm(CONTATO_VAZIO);
    setModal({ modo: "novo", contato: null });
  };

  const abrirEditar = (c) => {
    setForm(formDe(c));
    setModal({ modo: "editar", contato: c });
  };

  const salvar = async () => {
    setSalvando(true);
    try {
      if (modal.modo === "novo") {
        await criarContatoEmbarcador({ embarcadorId, ...form });
        addToast({ place: "tr", type: "success", title: "Contato criado" });
      } else {
        await atualizarContatoEmbarcador(modal.contato.id, { embarcadorId, ...form });
        addToast({ place: "tr", type: "success", title: "Contato atualizado" });
      }
      setModal(null);
      await carregar();
    } catch (err) {
      addToast({ place: "tr", type: "danger", title: "Não foi possível salvar o contato", description: mensagemErroContato(err) });
    } finally {
      setSalvando(false);
    }
  };

  const confirmarRemocao = async () => {
    setSalvando(true);
    try {
      await removerContatoEmbarcador(remover.id, embarcadorId);
      addToast({ place: "tr", type: "success", title: "Contato removido" });
      setRemover(null);
      await carregar();
    } catch (err) {
      addToast({ place: "tr", type: "danger", title: "Não foi possível remover o contato", description: mensagemErroContato(err) });
    } finally {
      setSalvando(false);
    }
  };

  const podeSalvar = form.nome.trim() && form.email.trim() && form.idTipoContato;

  const Raiz = embutido ? React.Fragment : "div";
  return (
    <Raiz {...(embutido ? {} : { className: "content" })}>
      {embutido ? null : (
        <PageHero
          eyebrow="Configurações"
          title="Contatos do embarcador"
          description="Quem recebe e-mails do embarcador sem precisar de usuário no Hub: caixas de grupo, TI, logística. O resumo diário de faturamento vai para os contatos do tipo Faturamento."
          actions={embarcadorId ? <PageHeroRefreshButton onClick={carregar} loading={loading} /> : null}
        />
      )}

      {!embarcadorId ? (
        <div style={{ marginTop: "1.25rem" }}>
          <SpecificEmpresaRequiredNotice message="Os contatos são por embarcador: escolha um embarcador específico no topo da página." />
        </div>
      ) : (
        <ShellCard
          title="Contatos"
          description={
            <span style={mutedStyle}>
              Quem recebe e-mails de <strong style={{ color: AUTOPILOT_COLORS.title }}>{nomeEmbarcador || `embarcador ${embarcadorId}`}</strong> sem precisar de usuário no Hub. O resumo diário de faturamento vai para os contatos do tipo Faturamento.
            </span>
          }
          action={
            <Button size="sm" color="info" onClick={abrirNovo}>
              + Adicionar
            </Button>
          }
        >
          {erro ? <div style={{ ...stateBoxStyle, color: AUTOPILOT_COLORS.statusDanger }}>{erro}</div> : null}
          {loading && contatos.length === 0 ? (
            <div style={stateBoxStyle}>
              <Spinner size="sm" />
              <span>Carregando…</span>
            </div>
          ) : null}
          {!loading && !erro && contatos.length === 0 ? <div style={stateBoxStyle}>Nenhum contato para este embarcador.</div> : null}
          {contatos.length > 0 ? (
            <Table size="sm" responsive hover className="hub-thead-accent">
              <thead className="text-primary">
                <tr>
                  <th>Tipo</th>
                  <th>Nome</th>
                  <th>E-mail</th>
                  <th>Telefone</th>
                  <th>Celular</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {contatos.map((c) => (
                  <tr key={c.id}>
                    <td>{c.tipoContato || c.idTipoContato}</td>
                    <td style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600 }}>{c.nome}</td>
                    <td>{c.email || "—"}</td>
                    <td>{telefone(c.dddTelefone, c.telefone) || "—"}</td>
                    <td>{telefone(c.dddCelular, c.celular) || "—"}</td>
                    <td className="text-right" style={{ whiteSpace: "nowrap" }}>
                      <Button size="sm" color="info" outline onClick={() => abrirEditar(c)}>
                        Editar
                      </Button>{" "}
                      <Button size="sm" color="danger" outline onClick={() => setRemover(c)} aria-label={`Remover ${c.nome}`}>
                        Remover
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : null}
        </ShellCard>
      )}

      <Modal isOpen={!!modal} toggle={salvando ? undefined : () => setModal(null)} modalClassName="modal-align-top" fade={false}>
        <ModalBody style={{ padding: "1.5rem" }}>
          <div style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600, marginBottom: "0.75rem" }}>
            {modal && modal.modo === "novo" ? "Novo contato" : "Editar contato"}
          </div>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label style={labelStyle}>Tipo *</Label>
                <Input type="select" value={form.idTipoContato} onChange={campo("idTipoContato")}>
                  <option value="">Selecione</option>
                  {tipos.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.nome}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label style={labelStyle}>Nome *</Label>
                <Input value={form.nome} maxLength={60} onChange={campo("nome")} />
              </FormGroup>
            </Col>
          </Row>
          <FormGroup>
            <Label style={labelStyle}>E-mail *</Label>
            <Input type="email" value={form.email} maxLength={100} onChange={campo("email")} />
          </FormGroup>
          <Row>
            <Col md="3">
              <FormGroup>
                <Label style={labelStyle}>DDD</Label>
                <Input value={form.dddTelefone} maxLength={4} onChange={campo("dddTelefone")} />
              </FormGroup>
            </Col>
            <Col md="9">
              <FormGroup>
                <Label style={labelStyle}>Telefone</Label>
                <Input value={form.telefone} maxLength={20} onChange={campo("telefone")} />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="3">
              <FormGroup>
                <Label style={labelStyle}>DDD</Label>
                <Input value={form.dddCelular} maxLength={4} onChange={campo("dddCelular")} />
              </FormGroup>
            </Col>
            <Col md="9">
              <FormGroup>
                <Label style={labelStyle}>Celular</Label>
                <Input value={form.celular} maxLength={20} onChange={campo("celular")} />
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setModal(null)} disabled={salvando}>
            Cancelar
          </Button>
          <Button color="info" onClick={salvar} disabled={salvando || !podeSalvar}>
            {salvando ? <Spinner size="sm" /> : "Salvar"}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={!!remover} toggle={salvando ? undefined : () => setRemover(null)} modalClassName="modal-align-top" fade={false}>
        <ModalBody style={{ padding: "1.5rem" }}>
          <div style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600, marginBottom: "0.5rem" }}>Remover contato</div>
          {remover ? (
            <div>
              <strong>{remover.nome}</strong> ({remover.email}) deixa de receber os e-mails do tipo {remover.tipoContato || "deste contato"}. Esta ação não pode ser desfeita.
            </div>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setRemover(null)} disabled={salvando}>
            Cancelar
          </Button>
          <Button color="danger" onClick={confirmarRemocao} disabled={salvando}>
            {salvando ? <Spinner size="sm" /> : "Remover"}
          </Button>
        </ModalFooter>
      </Modal>
    </Raiz>
  );
}
